import React, {useEffect, useState} from "react";
import {Modal, Radio, Space, Skeleton, Alert, Typography, Divider} from "antd";
import {useTranslation} from "react-i18next";
import api from "../api";
import useCurrentOrg from "../helpers/useCurrentOrg";

const {Text} = Typography;

const BulkCaiModal = ({
    open,
    onCancel,
    onOk,
    assetIds = [],
    cmfIds = []
}) => {
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg();
    const [loading, setLoading] = useState(true);
    const [previewData, setPreviewData] = useState(null);
    const [overwriteMode, setOverwriteMode] = useState('skip_existing');

    useEffect(() => {
        if (!open || assetIds.length === 0 || cmfIds.length === 0) {
            setLoading(false);
            return;
        }

        setLoading(true);
        api.post('/api/bulk_jobs/cai_preview', {
            asset_ids: assetIds,
            cmf_ids: cmfIds
        }).then(res => {
            setPreviewData(res.data);
            setLoading(false);
        }).catch(err => {
            console.error('Error fetching CAI preview:', err);
            setLoading(false);
        });
    }, [open, assetIds, cmfIds]);

    const handleOk = () => {
        onOk(overwriteMode);
    };

    const assetsWithValues = previewData?.summary?.assets_with_values || 0;
    const assetsWithoutValues = previewData?.summary?.assets_without_values || 0;
    const totalAssets = previewData?.total_assets || assetIds.length;

    return (
        <Modal
            title={t('run-ai-fields', 'Run AI Fields')}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            okText={t('run', 'Run')}
            cancelText={t('cancel', 'Cancel')}
            okButtonProps={{disabled: loading}}
            modalRender={(modal) => <div onClick={(e) => e.stopPropagation()}>{modal}</div>}
        >
            <Skeleton loading={loading}>
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <Text>
                        {t('running-ai-on-assets', 'Running AI analysis on {{count}} assets', {count: totalAssets})}
                    </Text>

                    {assetsWithValues > 0 && (
                        <Alert
                            type="info"
                            message={t('existing-values-found', 'Existing Values Found')}
                            description={
                                <>
                                    <Text>
                                        {t('assets-with-values', '{{count}} assets already have values for the selected AI fields.', {count: assetsWithValues})}
                                    </Text>
                                    <br/>
                                    <Text>
                                        {t('assets-without-values', '{{count}} assets have no existing values.', {count: assetsWithoutValues})}
                                    </Text>
                                </>
                            }
                        />
                    )}

                    <Divider />

                    <div>
                        <Text strong>{t('overwrite-mode', 'Overwrite Mode')}:</Text>
                        <Radio.Group
                            style={{display: 'block', marginTop: '12px'}}
                            value={overwriteMode}
                            onChange={(e) => setOverwriteMode(e.target.value)}
                        >
                            <Space direction="vertical">
                                <Radio value="overwrite">
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{t('overwrite-all', 'Overwrite All')}</Text>
                                        <Text type="secondary">
                                            {t('overwrite-all-description', 'Run AI on all {{count}} assets, replacing any existing values', {count: totalAssets})}
                                        </Text>
                                    </Space>
                                </Radio>
                                <Radio value="skip_existing">
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{t('skip-existing', 'Skip Existing')}</Text>
                                        <Text type="secondary">
                                            {t('skip-existing-description', 'Only run AI on {{count}} assets without existing values', {count: assetsWithoutValues})}
                                        </Text>
                                    </Space>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </div>

                    {previewData?.counts_by_field && Object.keys(previewData.counts_by_field).length > 1 && (
                        <>
                            <Divider />
                            <div>
                                <Text strong>{t('breakdown-by-field', 'Breakdown by Field')}:</Text>
                                <div style={{marginTop: '8px'}}>
                                    {Object.entries(previewData.counts_by_field).map(([id, field]) => (
                                        <div key={id} style={{marginBottom: '4px'}}>
                                            <Text>
                                                {field.name}: {field.with_values} {t('with-values', 'with values')}, {field.without_values} {t('without', 'without')}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </Space>
            </Skeleton>
        </Modal>
    );
};

export default BulkCaiModal;
